/**
 * Backend Service Layer - FIREBASE IMPLEMENTATION
 *
 * Connected to Firebase Auth and Firestore.
 */

import { auth, db, storage } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  getDoc,
  setDoc,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";

// Collection Names
const COLL = {
  RESELLERS: "users", // We store resellers as users with role='reseller'
  SALES: "sales",
  PAYMENTS: "payments",
  APPLICATIONS: "applications",
  JOB_APPLICATIONS: "job_applications",
  CLIENTS: "clients",
};

// --- Authentication Service ---
export const authService = {
  login: async (email, password) => {
    try {
      // 1. Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // 2. Check if user is an admin (separate collection)
      const adminDocRef = doc(db, "admins", user.uid);
      const adminDoc = await getDoc(adminDocRef);

      if (adminDoc.exists()) {
        // User is an admin
        const adminData = {
          id: user.uid,
          email: user.email,
          role: "admin",
          ...adminDoc.data(),
        };
        return { user: adminData };
      }

      // 3. Fetch extra user details (role, city, etc.) from Firestore
      const userDocRef = doc(db, COLL.RESELLERS, user.uid);
      const userDoc = await getDoc(userDocRef);

      let userData = {
        id: user.uid,
        email: user.email,
        role: "reseller", // Default
        name: user.displayName || "User",
      };

      if (userDoc.exists()) {
        userData = { ...userData, ...userDoc.data() };
      } else {
        // 3b. If not in Resellers/Users, check Clients (CRM)
        const clientDocRef = doc(db, COLL.CLIENTS, user.uid);
        const clientDoc = await getDoc(clientDocRef);

        if (clientDoc.exists()) {
          userData = { ...userData, ...clientDoc.data(), role: "client" };
        } else {
          // Fallback for Admin or New Reseller (Legacy)
          if (
            email.includes("admin") ||
            email === "muskan64401@gmail.com" ||
            email === "muskansoftwares@gmail.com"
          ) {
            userData.role = "admin";
          }
        }
      }

      if (userData.status === "blocked") {
        throw new Error("Your account has been suspended.");
      }

      return { user: userData };
    } catch (error) {
      console.error("Login Error:", error);
      throw new Error(error.message);
    }
  },

  register: async (email, password, name, phone, city) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      const userData = {
        email: email,
        name: name,
        phone: phone || "",
        city: city || "",
        role: "reseller",
        status: "pending", // Default to pending for KYC
        createdAt: new Date().toISOString(),
      };

      await setDoc(doc(db, COLL.RESELLERS, user.uid), userData);
      return { user: { id: user.uid, ...userData } };
    } catch (error) {
      console.error("Registration Error:", error);
      throw error;
    }
  },

  registerClient: async (email, password, name, phone) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      const clientData = {
        name: name,
        email: email,
        phone: phone || "",
        role: "client",
        status: "active",
        source: "online",
        balance: 0,
        createdAt: new Date().toISOString(),
      };

      await setDoc(doc(db, COLL.CLIENTS, user.uid), clientData);
      return { user: { id: user.uid, ...clientData } };
    } catch (error) {
      console.error("Client Registration Error:", error);
      throw error;
    }
  },

  logout: async () => {
    return signOut(auth);
  },

  getCurrentUser: async () => {
    return new Promise((resolve) => {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          const userDocRef = doc(db, COLL.RESELLERS, user.uid);
          const userDoc = await getDoc(userDocRef);
          let userData = { id: user.uid, email: user.email, role: "reseller" };
          if (userDoc.exists()) {
            userData = { ...userData, ...userDoc.data() };
          } else if (
            user.email.includes("admin") ||
            user.email === "muskan64401@gmail.com"
          ) {
            userData.role = "admin";
          }
          resolve(userData);
        } else {
          resolve(null);
        }
        unsubscribe();
      });
    });
  },
};

// --- Client Portal Services ---
export const clientService = {
  getMyProfile: async (email) => {
    try {
      const q = query(
        collection(db, COLL.CLIENTS),
        where("email", "==", email),
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }
      return null;
    } catch (error) {
      console.error("Error fetching client profile:", error);
      throw error;
    }
  },
};

// --- Public Service (No Auth Needed) ---
export const publicService = {
  uploadImage: async (file) => {
    try {
      // Create a reference to 'careers/filename'
      const storageRef = ref(storage, `careers/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error("Upload Error:", error);
      throw new Error("Image upload failed");
    }
  },

  submitJobApplication: async (applicationData) => {
    try {
      const docRef = await addDoc(collection(db, COLL.JOB_APPLICATIONS), {
        ...applicationData,
        status: "new", // new, hired, rejected
        date: new Date().toISOString(),
      });
      return { id: docRef.id, ...applicationData };
    } catch (error) {
      throw new Error("Failed to submit application: " + error.message);
    }
  },
};

// --- Reseller Service ---
export const resellerService = {
  // Submit new application (No Auth required)
  submitApplication: async (applicationData) => {
    try {
      const docRef = await addDoc(collection(db, COLL.APPLICATIONS), {
        ...applicationData,
        status: "pending",
        date: new Date().toISOString(),
      });
      return { id: docRef.id, ...applicationData };
    } catch (error) {
      throw new Error("Failed to submit application: " + error.message);
    }
  },

  // Get Reseller Stats
  getStats: async (resellerId) => {
    try {
      const qSales = query(
        collection(db, COLL.SALES),
        where("resellerId", "==", resellerId),
      );
      const salesSnapshot = await getDocs(qSales);
      let totalSales = 0;
      let totalCommission = 0;
      let pendingCommission = 0;

      salesSnapshot.forEach((doc) => {
        const sale = doc.data();
        totalSales++;
        if (sale.status === "approved") {
          totalCommission += Number(sale.commission) || 0;
        } else if (sale.status === "pending") {
          pendingCommission += Number(sale.commission) || 0;
        }
      });

      const qPayments = query(
        collection(db, COLL.PAYMENTS),
        where("resellerId", "==", resellerId),
        where("status", "==", "paid"),
      );
      const paymentsSnapshot = await getDocs(qPayments);
      let paidCommission = 0;
      paymentsSnapshot.forEach((doc) => {
        paidCommission += Number(doc.data().amount) || 0;
      });

      return { totalSales, totalCommission, pendingCommission, paidCommission };
    } catch (error) {
      console.error("Stats Error:", error);
      return {
        totalSales: 0,
        totalCommission: 0,
        pendingCommission: 0,
        paidCommission: 0,
      };
    }
  },

  submitSale: async (saleData) => {
    const newSale = {
      ...saleData,
      status: "pending",
      date: new Date().toISOString(),
      commission:
        Number(saleData.amount) * (Number(saleData.commissionRate) / 100),
    };
    const docRef = await addDoc(collection(db, COLL.SALES), newSale);
    return { id: docRef.id, ...newSale };
  },

  getSales: async (resellerId, filters = {}) => {
    let q = query(
      collection(db, COLL.SALES),
      where("resellerId", "==", resellerId),
    );
    if (filters.status) {
      q = query(q, where("status", "==", filters.status));
    }
    // Simple fetch without composite index for now, sort client side if needed
    const snapshot = await getDocs(q);
    let sales = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return sales.sort((a, b) => new Date(b.date) - new Date(a.date));
  },

  getPayments: async (resellerId) => {
    const q = query(
      collection(db, COLL.PAYMENTS),
      where("resellerId", "==", resellerId),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  },

  // --- AUTOMATED LICENSING & WALLET (PHP Backend) ---

  // Get Wallet Balance (and auto-create account)
  getWalletBalance: async (uid, name, email) => {
    try {
      const formData = new FormData();
      formData.append("action", "get_balance");
      formData.append("uid", uid);
      formData.append("name", name);
      formData.append("email", email);

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      return data.status === "success" ? Number(data.balance) : 0;
    } catch (error) {
      console.error("Wallet Balance Error:", error);
      return 0;
    }
  },

  // Generate a new license
  generateLicense: async (uid, softwareType) => {
    try {
      const formData = new FormData();
      formData.append("action", "generate_license");
      formData.append("uid", uid);
      formData.append("software_type", softwareType);

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.status === "success") {
        return data;
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  // Get Wallet History
  getWalletTransactions: async (uid) => {
    try {
      const formData = new FormData();
      formData.append("action", "get_transactions");
      formData.append("uid", uid);

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      return data.status === "success" ? data.data : [];
    } catch (error) {
      return [];
    }
  },

  // Get Generated Licenses History
  getGeneratedLicenses: async (uid) => {
    try {
      const formData = new FormData();
      formData.append("action", "get_license_history");
      formData.append("uid", uid);

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      return data.status === "success" ? data.data : [];
    } catch (error) {
      return [];
    }
  },

  getLeaderboard: async () => {
    try {
      // 1. Get all approved sales
      const q = query(
        collection(db, COLL.SALES),
        where("status", "==", "approved"),
      );
      const snapshot = await getDocs(q);

      const leaderboardMap = {};

      snapshot.forEach((doc) => {
        const sale = doc.data();
        if (sale.resellerId) {
          if (!leaderboardMap[sale.resellerId]) {
            leaderboardMap[sale.resellerId] = {
              id: sale.resellerId,
              name: sale.resellerName || "Reseller", // Fallback
              count: 0,
              totalCommission: 0,
            };
          }
          leaderboardMap[sale.resellerId].count += 1;
          leaderboardMap[sale.resellerId].totalCommission +=
            Number(sale.commission) || 0;

          // Try to update name if available and not 'Reseller'
          if (
            sale.resellerName &&
            leaderboardMap[sale.resellerId].name === "Reseller"
          ) {
            leaderboardMap[sale.resellerId].name = sale.resellerName;
          }
        }
      });

      // 2. Fetch User names for IDs that don't have names in sales (Legacy data fix)
      // This is expensive, so we'll skip it for now and rely on future sales having names,
      // or simple fallback.

      const leaderboard = Object.values(leaderboardMap)
        .sort((a, b) => b.totalCommission - a.totalCommission)
        .slice(0, 5); // Top 5

      return leaderboard;
    } catch (error) {
      console.error("Leaderboard Error:", error);
      return [];
    }
  },

  requestPayout: async (uid, amount, method, details) => {
    try {
      const formData = new FormData();
      formData.append("action", "request_payout");
      formData.append("uid", uid);
      formData.append("amount", amount);
      formData.append("method", method);
      formData.append("details", details);

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.status === "success") {
        return data;
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  getWithdrawals: async (uid) => {
    try {
      const formData = new FormData();
      formData.append("action", "get_withdrawals");
      formData.append("uid", uid);

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      return data.status === "success" ? data.data : [];
    } catch (error) {
      return [];
    }
  },
};

// --- Admin Service ---
export const adminService = {
  getStats: async () => {
    const resellersSnap = await getDocs(
      query(collection(db, COLL.RESELLERS), where("role", "==", "reseller")),
    );
    const salesSnap = await getDocs(collection(db, COLL.SALES));
    const paymentsSnap = await getDocs(collection(db, COLL.PAYMENTS));

    let totalRevenue = 0;
    salesSnap.forEach((doc) => {
      if (doc.data().status === "approved")
        totalRevenue += Number(doc.data().amount) || 0;
    });

    let pendingPayments = 0;
    paymentsSnap.forEach((doc) => {
      if (doc.data().status === "pending")
        pendingPayments += Number(doc.data().amount) || 0;
    });

    return {
      totalResellers: resellersSnap.size,
      totalSales: salesSnap.size,
      totalRevenue,
      pendingPayments,
    };
  },

  getResellers: async () => {
    // 1. Fetch valid registered resellers
    const qUsers = query(
      collection(db, COLL.RESELLERS),
      where("role", "==", "reseller"),
    );
    const usersSnap = await getDocs(qUsers);
    const users = usersSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      source: "registered",
    }));

    // 2. Fetch approved applications (Safest Method: Fetch All & Filter)
    // This avoids Firestore Indexing issues with '!=' or '==' if indexes are missing.
    const qApps = query(collection(db, COLL.APPLICATIONS));
    const appsSnap = await getDocs(qApps);

    const approvedApps = appsSnap.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter(
        (app) => app.status && String(app.status).toLowerCase() === "approved",
      )
      .map((app) => ({
        ...app,
        role: "reseller",
        source: "application",
      }));

    // 3. Merge: If an email exists in 'users', trust 'users'. If only in 'apps', include it.
    const mergedMap = new Map();

    // Add Approved Apps first
    approvedApps.forEach((app) => {
      if (app.email) mergedMap.set(app.email, app);
    });

    // Overwrite with Registered Users (they are the source of truth for accounts)
    users.forEach((user) => {
      if (user.email) mergedMap.set(user.email, user);
    });

    return Array.from(mergedMap.values());
  },

  getApplications: async () => {
    const q = query(
      collection(db, COLL.APPLICATIONS),
      where("status", "==", "pending"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  },

  getJobApplications: async () => {
    const q = query(collection(db, COLL.JOB_APPLICATIONS));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  },

  getTeamMembers: async () => {
    // Get all applicants who are 'hired'
    const q = query(
      collection(db, COLL.JOB_APPLICATIONS),
      where("status", "==", "hired"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  },

  hireApplicant: async (applicantId) => {
    const appRef = doc(db, COLL.JOB_APPLICATIONS, applicantId);
    await updateDoc(appRef, {
      status: "hired",
      hiredDate: new Date().toISOString(),
    });
    return { id: applicantId, status: "hired" };
  },

  approveApplication: async (applicationId) => {
    const appRef = doc(db, COLL.APPLICATIONS, applicationId);
    await updateDoc(appRef, { status: "approved" });
    return { success: true, message: "Application Approved. Mark as Done." };
  },

  verifyReseller: async (resellerId) => {
    const userRef = doc(db, COLL.RESELLERS, resellerId);
    await updateDoc(userRef, {
      status: "active",
      verifiedAt: new Date().toISOString(),
    });

    // Notification
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (userData.email) {
          const body = `
            <p>Dear ${userData.name},</p>
            <p><strong>Congratulations!</strong> Your Reseller Account has been verified.</p>
            <p>You can now generate licenses and access the marketing kit to boost your sales.</p>
            <p><a href="https://muskansoftwares.com/reseller/login" class="btn">Go to Dashboard</a></p>
          `;
          await adminService.sendEmailNotification(
            userData.email,
            "Account Verified - Muskan Softwares",
            body,
          );
        }
      }
    } catch (e) {
      console.error("Email Error:", e);
    }

    return { id: resellerId, status: "active" };
  },

  getSales: async (filters = {}) => {
    let salesRef = collection(db, COLL.SALES);
    let q = query(salesRef);
    if (filters.status) {
      q = query(salesRef, where("status", "==", filters.status));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  },

  approveSale: async (saleId) => {
    const saleRef = doc(db, COLL.SALES, saleId);
    await updateDoc(saleRef, {
      status: "approved",
      approvedDate: new Date().toISOString(),
    });

    // Notification
    try {
      const saleSnap = await getDoc(saleRef);
      if (saleSnap.exists()) {
        const saleData = saleSnap.data();
        const resellerId = saleData.resellerId;

        if (resellerId) {
          const usersRef = doc(db, COLL.RESELLERS, resellerId);
          const userSnap = await getDoc(usersRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.email) {
              const body = `
                  <p>Dear ${userData.name},</p>
                  <p>Great news! Your sale for <strong>${saleData.software}</strong> has been approved.</p>
                  <p><strong>Commission Earned:</strong> Rs. ${saleData.commission}</p>
                  <p>Keep up the great work!</p>
                `;
              await adminService.sendEmailNotification(
                userData.email,
                "Commission Approved! 💰",
                body,
              );
            }
          }
        }
      }
    } catch (e) {
      console.error("Email Error:", e);
    }

    return { id: saleId, status: "approved" };
  },

  sendEmailNotification: async (
    to,
    subject,
    message,
    isHtmlTemplate = false,
  ) => {
    try {
      const formData = new FormData();
      formData.append("action", "send_email");
      formData.append("to", to);
      formData.append("subject", subject);
      formData.append("message", message);
      if (isHtmlTemplate) {
        formData.append("is_html_template", "1");
      }

      await fetch("/api/reseller_api.php", { method: "POST", body: formData });
    } catch (e) {
      console.error("Send Email Failed", e);
    }
  },

  rejectSale: async (saleId, reason) => {
    const saleRef = doc(db, COLL.SALES, saleId);
    await updateDoc(saleRef, {
      status: "rejected",
      rejectionReason: reason,
    });
    return { id: saleId, status: "rejected" };
  },

  getPayments: async (filters = {}) => {
    let paymentsRef = collection(db, COLL.PAYMENTS);
    let q = query(paymentsRef);
    if (filters.status) {
      q = query(paymentsRef, where("status", "==", filters.status));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  },

  approvePayment: async (paymentId) => {
    const payRef = doc(db, COLL.PAYMENTS, paymentId);
    await updateDoc(payRef, { status: "approved" });
    return { id: paymentId, status: "approved" };
  },

  markPaymentPaid: async (paymentId, transactionId) => {
    const payRef = doc(db, COLL.PAYMENTS, paymentId);
    await updateDoc(payRef, {
      status: "paid",
      transactionId: transactionId,
      paidDate: new Date().toISOString(),
    });
    return { id: paymentId, status: "paid" };
  },

  getBackups: async () => {
    try {
      const response = await fetch("/api/list_backups.php");
      if (!response.ok) throw new Error("API failed");
      return await response.json();
    } catch (error) {
      console.error("Backup Sync Failed:", error);
      return [];
    }
  },

  getLicenseActivations: async () => {
    try {
      // Fetch from PHP API tracking activations
      const response = await fetch("/api/list_licenses.php");
      if (!response.ok) return []; // Graceful fallback
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn("License Fetch Failed:", error);
      return [];
    }
  },

  getDownloads: async () => {
    try {
      // Fetch download stats
      const response = await fetch("/api/list_downloads.php");
      if (!response.ok) {
        // Mock data if API missing
        return { total: 0, products: [] };
      }
      return await response.json();
    } catch (error) {
      return { total: 0, products: [] };
    }
  },

  // --- ADMIN WALLET MANAGEMENT (PHP API) ---

  getResellerWallets: async () => {
    try {
      const formData = new FormData();
      formData.append("action", "admin_get_wallets");

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      return data.status === "success" ? data.data : [];
    } catch (error) {
      console.error("Admin Wallet Fetch Error:", error);
      return [];
    }
  },

  manageWalletFunds: async (
    resellerId,
    amount,
    type,
    description,
    profileData = {},
  ) => {
    try {
      const formData = new FormData();
      formData.append("action", "admin_add_funds");
      formData.append("reseller_id", resellerId || "");
      formData.append("amount", amount);
      formData.append("type", type);
      formData.append("description", description);

      if (profileData.email) formData.append("email", profileData.email);
      if (profileData.name) formData.append("name", profileData.name);

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.status === "success") {
        return data;
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  getWithdrawals: async () => {
    try {
      const formData = new FormData();
      formData.append("action", "admin_get_withdrawals");

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      return data.status === "success" ? data.data : [];
    } catch (error) {
      return [];
    }
  },

  updateWithdrawalStatus: async (
    withdrawalId,
    status,
    note,
    proofFile = null,
  ) => {
    try {
      const formData = new FormData();
      formData.append("action", "admin_update_withdrawal");
      formData.append("withdrawal_id", withdrawalId);
      formData.append("status", status);
      formData.append("note", note);
      if (proofFile) {
        formData.append("proof_image", proofFile);
      }

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (data.status === "success") return data;
      throw new Error(data.message);
    } catch (error) {
      throw error;
    }
  },

  getSchoolProFeedbacks: async () => {
    try {
      const q = query(collection(db, "feedback"), orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);

      // Fetch feedback data with admin emails
      const feedbacksWithEmails = await Promise.all(
        snapshot.docs.map(async (feedbackDoc) => {
          const feedbackData = { id: feedbackDoc.id, ...feedbackDoc.data() };

          // Fetch school document to get admin email
          if (feedbackData.school_id) {
            try {
              const schoolRef = doc(db, "schools", feedbackData.school_id);
              const schoolDoc = await getDoc(schoolRef);

              if (schoolDoc.exists()) {
                const schoolData = schoolDoc.data();
                feedbackData.admin_email = schoolData.admin_email || null;
                feedbackData.admin_name = schoolData.admin_name || null;
              }
            } catch (err) {
              console.warn(
                `Failed to fetch school data for ${feedbackData.school_id}:`,
                err,
              );
            }
          }

          return feedbackData;
        }),
      );

      return feedbacksWithEmails;
    } catch (error) {
      console.error("Feedback Fetch Error:", error);
      return [];
    }
  },

  getDistroFeedbacks: async () => {
    try {
      const q = query(
        collection(db, "distro_feedback"),
        orderBy("timestamp", "desc"),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Distro Feedback Fetch Error:", error);
      return [];
    }
  },

  getSchoolProFeedbacksRealtime: (callback) => {
    const q = query(collection(db, "feedback"), orderBy("timestamp", "desc"));
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(data);
    });
  },

  getDistroFeedbacksRealtime: (callback) => {
    const q = query(
      collection(db, "distro_feedback"),
      orderBy("timestamp", "desc"),
    );
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(data);
    });
  },

  getRetailProFeedbacksRealtime: (callback) => {
    const q = query(
      collection(db, "retailpro_feedback"),
      orderBy("timestamp", "desc"),
    );
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(data);
    });
  },

  resolveFeedback: async (feedbackId, adminEmail, schoolName) => {
    try {
      // 1. Update feedback status in Firestore
      const feedbackRef = doc(db, "feedback", feedbackId);
      await updateDoc(feedbackRef, {
        status: "resolved",
        resolved_at: new Date().toISOString(),
        resolved_by: "admin",
      });

      // 2. Send email notification to client
      const emailBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9fafb; border-radius: 10px;">
          <div style="background-color: #E91E63; padding: 20px; border-radius: 10px 10px 0 0; text-align: center;">
            <img src="https://muskansoftwares.com/logo192.png" alt="Muskan Softwares" style="width: 60px; height: 60px; margin-bottom: 10px; border-radius: 50%;">
            <h1 style="color: white; margin: 0; font-size: 24px;">Muskan Softwares</h1>
          </div>

          <div style="background-color: white; padding: 30px; border-radius: 0 0 10px 10px;">
            <h2 style="color: #1f2937; margin-top: 0;">Your Feedback Has Been Addressed! ✅</h2>

            <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
              Dear <strong>${schoolName}</strong>,
            </p>

            <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
              We're pleased to inform you that the feedback you submitted has been successfully resolved by our team.
            </p>

            <div style="background-color: #f3f4f6; padding: 15px; border-left: 4px solid #E91E63; margin: 20px 0;">
              <p style="color: #374151; margin: 0; font-size: 14px;">
                <strong>Status:</strong> Resolved<br>
                <strong>Date:</strong> ${new Date().toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </p>
            </div>

            <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
              If you have any further questions or concerns, please don't hesitate to reach out to us.
            </p>

            <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
              Thank you for helping us improve our software!
            </p>

            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
              <p style="color: #9ca3af; font-size: 12px; margin: 0;">
                Best Regards,<br>
                <strong style="color: #E91E63;">Muskan Softwares Team</strong><br>
                <a href="https://muskansoftwares.com" style="color: #E91E63; text-decoration: none;">www.muskansoftwares.com</a>
              </p>
            </div>
          </div>
        </div>
      `;

      await adminService.sendEmailNotification(
        adminEmail, // Revert to using the argument directly
        "Your Feedback Has Been Resolved - Muskan Softwares",
        emailBody,
        true, // isHtmlTemplate = true
      );

      return { success: true };
    } catch (error) {
      console.error("Resolve Feedback Error:", error);
      throw new Error("Failed to resolve feedback: " + error.message);
    }
  },

  generateLicense: async (softwareType, resellerId = 0, machineId = "") => {
    try {
      const formData = new FormData();
      formData.append("action", "admin_generate_license");
      formData.append("software_type", softwareType);
      formData.append("reseller_id", resellerId);
      if (machineId) formData.append("machine_id", machineId);

      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.status === "success") {
        return data;
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  recordPetrolPumpLicense: async (data) => {
    try {
      const docRef = await addDoc(collection(db, "petrol_licenses"), {
        ...data,
        createdAt: new Date().toISOString(),
      });
      return { id: docRef.id, ...data };
    } catch (error) {
      console.error("Error recording license:", error);
      throw error;
    }
  },

  getPetrolPumpLicenses: async () => {
    try {
      const q = query(collection(db, "petrol_licenses"));
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (error) {
      console.error("Error fetching licenses:", error);
      return [];
    }
  },

  getNotifications: async () => {
    try {
      const formData = new FormData();
      formData.append("action", "get_notifications");
      formData.append("is_admin", "1");
      const response = await fetch("/api/reseller_api.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      return data.status === "success" ? data : { data: [], unread: 0 };
    } catch (e) {
      return { data: [], unread: 0 };
    }
  },

  markRead: async (notifId = "all") => {
    try {
      const formData = new FormData();
      formData.append("action", "mark_notification_read");
      formData.append("is_admin", "1");
      formData.append("notif_id", notifId);
      await fetch("/api/reseller_api.php", { method: "POST", body: formData });
    } catch (e) {}
  },

  // --- CLIENT MANAGEMENT (CRM) ---

  getClients: async () => {
    const q = query(collection(db, COLL.CLIENTS));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  },

  createClient: async (clientData) => {
    // Handle initial balance
    const initialBalance = Number(clientData.balance || 0);
    const transactions = [];

    if (initialBalance !== 0) {
      transactions.push({
        id: Date.now(),
        date: new Date().toISOString(),
        type: initialBalance > 0 ? "debit" : "credit", // Positive means they OWE us (Debit)
        description: "Opening Balance",
        amount: Math.abs(initialBalance),
      });
    }

    const newClient = {
      ...clientData,
      createdAt: new Date().toISOString(),
      balance: initialBalance,
      logs: [],
      transactions: transactions,
      services: [],
    };
    const docRef = await addDoc(collection(db, COLL.CLIENTS), newClient);
    return { id: docRef.id, ...newClient };
  },

  updateClient: async (clientId, updateData) => {
    const clientRef = doc(db, COLL.CLIENTS, clientId);
    await updateDoc(clientRef, updateData);
    return { id: clientId, ...updateData };
  },

  addClientLog: async (clientId, note) => {
    const clientRef = doc(db, COLL.CLIENTS, clientId);
    const clientSnap = await getDoc(clientRef);
    if (!clientSnap.exists()) throw new Error("Client not found");

    const logs = clientSnap.data().logs || [];
    const newLog = {
      id: Date.now(),
      date: new Date().toISOString(),
      note: note,
    };

    await updateDoc(clientRef, { logs: [newLog, ...logs] });
    return newLog;
  },

  addClientTransaction: async (clientId, transaction) => {
    const clientRef = doc(db, COLL.CLIENTS, clientId);
    const clientSnap = await getDoc(clientRef);
    if (!clientSnap.exists()) throw new Error("Client not found");

    const currentData = clientSnap.data();
    const transactions = currentData.transactions || [];

    // Calculate new balance
    // Credit (Received) decreases balance (client owes less)
    // Debit (Bill) increases balance (client owes more)
    let amount = Number(transaction.amount);
    let newBalance = Number(currentData.balance || 0);

    if (transaction.type === "debit") {
      // Bill
      newBalance += amount;
    } else {
      // Credit/Received
      newBalance -= amount;
    }

    const newTransaction = {
      id: Date.now(),
      date: new Date().toISOString(),
      ...transaction,
    };

    await updateDoc(clientRef, {
      transactions: [newTransaction, ...transactions],
      balance: newBalance,
    });

    return { transaction: newTransaction, newBalance };
  },

  // --- POS USERS MANAGEMENT ---

  getPosUsers: async () => {
    try {
      const q = query(collection(db, "retailpro_users"));
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const dateA =
            a.requestedAt?.toDate?.() || new Date(a.requestedAt || 0);
          const dateB =
            b.requestedAt?.toDate?.() || new Date(b.requestedAt || 0);
          return dateB - dateA;
        });
    } catch (error) {
      console.error("Error getting POS users:", error);
      return [];
    }
  },

  getPendingPosUsers: async () => {
    try {
      const q = query(
        collection(db, "retailpro_users"),
        where("status", "==", "pending"),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting pending POS users:", error);
      return [];
    }
  },

  approvePosUser: async (userId) => {
    try {
      const userRef = doc(db, "retailpro_users", userId);
      await updateDoc(userRef, {
        status: "approved",
        approvedAt: new Date().toISOString(),
      });
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  rejectPosUser: async (userId, reason = "") => {
    try {
      const userRef = doc(db, "retailpro_users", userId);
      await updateDoc(userRef, {
        status: "rejected",
        rejectedAt: new Date().toISOString(),
        rejectionReason: reason,
      });
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  suspendPosUser: async (userId) => {
    try {
      const userRef = doc(db, "retailpro_users", userId);
      await updateDoc(userRef, {
        status: "suspended",
      });
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  // --- SCHOOLS MANAGEMENT ---
  getSchools: async () => {
    try {
      const q = query(collection(db, "schools"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting schools:", error);
      return [];
    }
  },

  getDistros: async () => {
    try {
      const q = query(collection(db, "distros"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting distros:", error);
      return [];
    }
  },
};

// Initialize
export const initializeBackend = () => {
  console.log("Firebase Backend Initialized");
};
